import moment from 'moment';

export type ConversionKeys =
  | 'y'
  | 'M'
  | 'w'
  | 'd'
  | 'hh'
  | 'mm'
  | 'ss'
  | 'ms'
  | 'h'
  | 'm'
  | 's';

type KeyConversionMap = {
  [k in ConversionKeys]: (duration: moment.Duration, trim?: boolean) => string;
};

const keyMap: KeyConversionMap = {
  // Years parser
  y: (duration) => Math.floor(duration.asYears()).toFixed(),
  // Months parser
  M: (duration, trim) =>
    Math.floor(trim ? duration.asMonths() : duration.months()).toFixed(),
  // Weeks parser
  w: (duration, trim) =>
    Math.floor(trim ? duration.asWeeks() : duration.weeks()).toFixed(),
  // Days parser
  d: (duration, trim) =>
    Math.floor(trim ? duration.asDays() : duration.asDays()).toFixed(),
  // Hours parsers
  hh: (duration, trim) => {
    const value = Math.floor(
      trim ? duration.asHours() : duration.asHours() % 24,
    );
    return value < 10 ? `0${value.toFixed()}` : value.toFixed();
  },
  h: (duration, trim) =>
    Math.floor(trim ? duration.asHours() : duration.asHours() % 24).toFixed(),
  // Minutes parsers
  mm: (duration, trim) => {
    const value = Math.floor(
      trim ? duration.asMinutes() : duration.asMinutes() % 60,
    );
    return value < 10 ? `0${value.toFixed()}` : value.toFixed();
  },
  m: (duration, trim) =>
    Math.floor(
      trim ? duration.asMinutes() : duration.asMinutes() % 60,
    ).toFixed(),
  // Seconds parsers
  ss: (duration, trim) => {
    const value = Math.floor(
      trim ? duration.asSeconds() : duration.asSeconds() % 60,
    );
    return value < 10 ? `0${value.toFixed()}` : value.toFixed();
  },
  s: (duration, trim) =>
    Math.floor(
      trim ? duration.asSeconds() : duration.asSeconds() % 60,
    ).toFixed(),
  // Milliseconds parser
  ms: (duration, trim) =>
    Math.floor(
      trim ? duration.asMilliseconds() : duration.asMilliseconds() % 1000,
    ).toFixed(),
};

/**
 * Formats a moment duration using a custom format:
 * y - years
 * M - months
 * w - weeks
 * d - days
 * h - hours
 * hh - hours padded with 0
 * m - minutes
 * mm - minutes padded with 0
 * s - seconds
 * ss - seconds padded with 0
 * ms - milliseconds
 *
 * For custom strings, wrap it in [], i.ex.: "D [days]". ":" and "-" are recognized by default
 * and don't need escape.
 * @param duration
 * @param format
 */
export const formatDuration = (
  duration: moment.Duration,
  format: string,
): string => {
  const matches = format.matchAll(
    /(?:\[([\w\s]+)\])*(y|M|w|d|hh|mm|ss|ms|h|m|s)*/g,
  );

  const isNegative = duration.asMilliseconds() < 0;
  const signal = isNegative ? '-' : '';
  const positiveValue = isNegative
    ? moment.duration(duration.asMilliseconds() * -1)
    : duration;

  let keyFound = false;
  return (
    signal +
    Array.from(matches)
      .filter((m) => m[0] !== '')
      .reduce((acc, match) => {
        if (!match[2]) {
          return acc.replace(match[0], match[1]);
        }
        const value = keyMap[match[2] as ConversionKeys](
          positiveValue,
          !keyFound,
        );
        keyFound = true;
        return acc.replace(match[0], value);
      }, format)
  );
};

//
// Date display utils - For dashboard use
//

export type DateTruncateOptions =
  | 'CREATED_AT_TRUNCATED_TO_MONTH'
  | 'CREATED_AT_TRUNCATED_TO_WEEK'
  | 'CREATED_AT_TRUNCATED_TO_DAY';

export interface ParsedDate {
  id: string;
  display: string;
}

interface DateFilter {
  lessThanOrEqualTo: string;
  greaterThanOrEqualTo: string;
}

/**
 * Checks if bound was reached in the loop.
 */
const isDateBoundReached = (
  currentPos: moment.Moment,
  bound: moment.Moment,
  order: 'asc' | 'desc',
) =>
  order === 'desc' ? currentPos.isBefore(bound) : currentPos.isAfter(bound);

/**
 * Retrieves the DateTruncateOptions from the number of days
 */
const getDateTruncateFromDays = (days: number): DateTruncateOptions => {
  if (days > 42) {
    return 'CREATED_AT_TRUNCATED_TO_MONTH';
  }
  if (days > 7 && days <= 42) {
    return 'CREATED_AT_TRUNCATED_TO_WEEK';
  }
  return 'CREATED_AT_TRUNCATED_TO_DAY';
};

/**
 * Retrieves the trucate mode to apply to dates in an aggregation
 * @param filter the date filter
 * @returns
 */
export const getDateTruncate = (filter: DateFilter): DateTruncateOptions => {
  const from = moment(filter.greaterThanOrEqualTo);
  const to = moment(filter.lessThanOrEqualTo);
  const duration = moment.duration(to.diff(from));

  return getDateTruncateFromDays(duration.asDays());
};

/**
 * Get the date format to pass to moment based on the truncate option.
 * @param truncate
 * @returns
 */
const getDateKeyFormat = (truncate?: DateTruncateOptions): string => {
  switch (truncate) {
    case 'CREATED_AT_TRUNCATED_TO_MONTH':
      return 'MM/YYYY';
    case 'CREATED_AT_TRUNCATED_TO_WEEK':
      return 'YYYY-[W]WW';
    case 'CREATED_AT_TRUNCATED_TO_DAY':
    default:
      return 'DD/MM/YYYY';
  }
};

/**
 * Get the date format to pass to moment based on the truncate option.
 * @param truncate
 * @returns
 */
export const formatDateKey = (filter: DateFilter, value: string): string => {
  const from = moment(filter.greaterThanOrEqualTo);
  const to = moment(filter.lessThanOrEqualTo);
  const duration = moment.duration(to.diff(from));

  // Since here we are only working with dates, remove the time date if it exists
  const format = getDateKeyFormat(getDateTruncateFromDays(duration.asDays()));
  return moment(value).format(format);
};

/**
 * Get the date format to pass to moment based on the truncate option.
 * @param truncate
 * @returns
 */
export const formatDateDisplay = (
  value: moment.Moment,
  bounds: { start: moment.Moment; end: moment.Moment },
  truncate?: DateTruncateOptions,
): string => {
  switch (truncate) {
    case 'CREATED_AT_TRUNCATED_TO_MONTH': {
      const dateStr = value.format('MMM/YY');
      return dateStr.charAt(0).toUpperCase() + dateStr.slice(1);
    }
    case 'CREATED_AT_TRUNCATED_TO_WEEK': {
      let start = value.clone().startOf('isoWeek');
      start = start.isAfter(bounds.start) ? start : bounds.start;
      let end = value.clone().endOf('isoWeek');
      end = end.isBefore(bounds.end) ? end : bounds.end;

      return `${start.format('DD/MM')} a ${end.format('DD/MM')}`;
    }
    case 'CREATED_AT_TRUNCATED_TO_DAY':
    default:
      return value.format('DD/MM');
  }
};

/**
 * Expand array of date options based on the filter
 * @param filter
 * @param order
 * @returns
 */
export const getDateOptions = (
  filter: DateFilter,
  order: 'asc' | 'desc' = 'asc',
  upToCurrentDate = false,
): Array<ParsedDate> => {
  const from = moment(filter.greaterThanOrEqualTo);
  const to = moment(filter.lessThanOrEqualTo);
  const duration = moment.duration(to.diff(from));
  const truncate = getDateTruncateFromDays(duration.asDays());
  const dateFormat = getDateKeyFormat(truncate);

  let dateKey: moment.unitOfTime.DurationConstructor = 'days';
  if (truncate === 'CREATED_AT_TRUNCATED_TO_WEEK') {
    dateKey = 'weeks';
  } else if (truncate === 'CREATED_AT_TRUNCATED_TO_MONTH') {
    dateKey = 'months';
  }

  // When `upToCurrentDate` is set, never generate periods past today, so we
  // don't render empty columns for months that haven't happened yet. The
  // truncation/granularity is still derived from the full filter range above.
  const effectiveTo = upToCurrentDate && moment().isBefore(to) ? moment() : to;

  // Populate the date array
  const dateArr: Array<ParsedDate> = [];
  const lastDate = order === 'desc' ? from : effectiveTo;
  const currentDate = order === 'desc' ? effectiveTo : from;
  while (!isDateBoundReached(currentDate, lastDate, order)) {
    const pos = currentDate.clone();
    dateArr.push({
      id: pos.format(dateFormat),
      display: formatDateDisplay(pos, { start: from, end: to }, truncate),
    });

    if (truncate === 'CREATED_AT_TRUNCATED_TO_WEEK') {
      currentDate[order === 'desc' ? 'startOf' : 'endOf']('isoWeek');
      currentDate[order === 'desc' ? 'subtract' : 'add'](1, 'days');
    } else {
      currentDate[order === 'desc' ? 'subtract' : 'add'](1, dateKey);
    }
  }

  return dateArr;
};

//
// Time conversions
//

export type TimeUnit = 'hours' | 'minutes' | 'seconds' | 'milliseconds';

const conversionFactorToMs = (unit: TimeUnit) => {
  switch (unit) {
    case 'hours':
      return 3600000;
    case 'minutes':
      return 60000;
    case 'seconds':
      return 1000;
    default:
      return 1;
  }
};

export const convertTime = (
  value: number,
  fromUnit: TimeUnit | undefined = 'seconds',
  toUnit: TimeUnit | undefined = 'milliseconds',
): number => {
  const fromConversionFactor = conversionFactorToMs(fromUnit);
  const toConversionFactor = conversionFactorToMs(toUnit);
  return (value * fromConversionFactor) / toConversionFactor;
};
